import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { tap, catchError, switchMap } from 'rxjs/operators';
import { throwError } from 'rxjs';

import { isAzureAdEnabled } from './azure-ad-env';
import { AuthService } from './auth.service';
import { AuthLoggerService } from './auth-logger.service';
import { AuthSessionService } from './auth-session.service';

/** On cloned request after one refresh — avoids infinite 401/403 → refresh loops. */
const SPICE_JWT_RETRY_HEADER = 'X-SPICE-JWT-RETRY';

/** Persist any new access_token returned in API responses so localStorage stays in sync. */
function persistTokenFromResponse(auth: AuthService, body: unknown): void {
  if (body == null || typeof body !== 'object') return;
  const b = body as Record<string, unknown>;
  const newToken = b['access_token'] ?? b['token'];
  if (typeof newToken === 'string' && newToken.trim()) {
    auth.setToken(newToken.trim());
  }
}

function isLoginOrRefresh(url: string, auth: AuthService): boolean {
  return url.includes('/api/v1/login') || url.includes(auth.getRefreshPath());
}

/** Any successful API call while logged in counts as session activity (idle clock + proactive refresh). */
function bumpSessionOnSuccessfulRequest(
  auth: AuthService,
  session: AuthSessionService,
  reqUrl: string,
  event: HttpEvent<unknown>
): void {
  if (!(event instanceof HttpResponse) || !event.ok) return;
  if (isLoginOrRefresh(reqUrl, auth)) return;
  if (!auth.isAuthenticated()) return;
  session.signalUserActivity();
}

function attachJwtAndForward(
  req: Parameters<HttpInterceptorFn>[0],
  next: Parameters<HttpInterceptorFn>[1],
  auth: AuthService,
  authLogger: AuthLoggerService,
  router: Router,
  authSession: AuthSessionService,
  token: string
) {
  const cloned = req.clone({
    setHeaders: { Authorization: `Bearer ${token}` }
  });

  return next(cloned).pipe(
    tap((event) => {
      bumpSessionOnSuccessfulRequest(auth, authSession, req.url, event);
      if (event instanceof HttpResponse && event.body) {
        persistTokenFromResponse(auth, event.body);
      }
    }),
    catchError((err: unknown) => {
      const response = err instanceof HttpErrorResponse ? err : null;
      if (response?.status === 401 && !isLoginOrRefresh(req.url, auth)) {
        authLogger.log('401_ERROR', { url: req.url });
      }
      const isAuthRetry = req.headers.get(SPICE_JWT_RETRY_HEADER) === '1';
      const retriableAuth =
        (response?.status === 401 || response?.status === 403) &&
        !isAuthRetry &&
        !isLoginOrRefresh(req.url, auth);
      /** 403: many gateways/backends return this for an expired or rejected JWT, not only “forbidden by role.” */
      if (!retriableAuth) {
        return throwError(() => err);
      }
      if (response?.status === 403) {
        authLogger.log('403_RETRIABLE', { url: req.url });
      }
      /** Shared refresh + queue: 401/403 (password JWT) — one retry with {@link SPICE_JWT_RETRY_HEADER}. */
      return auth.refreshToken().pipe(
        switchMap((newToken) => {
          if (!newToken) {
            /** Session cleared in AuthService.logoutAfterRefreshFailure — navigate only. */
            void router.navigateByUrl('/login');
            return throwError(() => err);
          }
          const retryReq = req.clone({
            setHeaders: { Authorization: `Bearer ${newToken}`, [SPICE_JWT_RETRY_HEADER]: '1' }
          });
          return next(retryReq).pipe(
            tap((event) => {
              bumpSessionOnSuccessfulRequest(auth, authSession, retryReq.url, event);
              if (event instanceof HttpResponse && event.body) {
                persistTokenFromResponse(auth, event.body);
              }
            })
          );
        }),
        catchError(() => {
          void router.navigateByUrl('/login');
          return throwError(() => err);
        })
      );
    })
  );
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const authLogger = inject(AuthLoggerService);
  const authSession = inject(AuthSessionService);
  const router = inject(Router);

  /**
   * When Entra is enabled, {@link azureAdApiInterceptor} may already have set `Authorization` (MSAL).
   * Password / SPICE sessions only have a JWT in {@link AuthService.getToken} — attach it when no
   * upstream bearer is present so both SSO and manual login work against the same API.
   */
  if (isAzureAdEnabled()) {
    if (req.headers.has('Authorization')) {
      return next(req).pipe(
        tap((event) => {
          bumpSessionOnSuccessfulRequest(auth, authSession, req.url, event);
          if (event instanceof HttpResponse && event.body) {
            persistTokenFromResponse(auth, event.body);
          }
        })
      );
    }

    const token = auth.getToken();
    if (isLoginOrRefresh(req.url, auth) || !token) {
      return next(req).pipe(
        tap((event) => {
          bumpSessionOnSuccessfulRequest(auth, authSession, req.url, event);
          if (event instanceof HttpResponse && event.body) {
            persistTokenFromResponse(auth, event.body);
          }
        })
      );
    }

    return attachJwtAndForward(req, next, auth, authLogger, router, authSession, token);
  }

  const token = auth.getToken();

  /** Login and refresh must not send Bearer access_token (refresh uses body.refresh_token). */
  if (isLoginOrRefresh(req.url, auth) || !token) {
    return next(req).pipe(
      tap((event) => {
        bumpSessionOnSuccessfulRequest(auth, authSession, req.url, event);
        if (event instanceof HttpResponse && event.body) {
          persistTokenFromResponse(auth, event.body);
        }
      })
    );
  }

  return attachJwtAndForward(req, next, auth, authLogger, router, authSession, token);
};
